import {MutableRefObject, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useParams, useRouteMatch} from 'react-router-dom';
import {UserType} from '../../../../../../backend/enums/user-type';
import {ApplicationUser} from '../../../../../../backend/responses/application-user';
import {PaginatedTable} from '../../../../../../components/paginated-table/paginated-table';
import {useOffice} from '../../../../../../contexts/office/context';
import {useSnackBar} from '../../../../../../contexts/snack-bar/context';
import {ScreenPaths} from '../../../../../../enums/screen-path';
import {usePaginatedTable} from '../../../../../../hooks/paginated-table';
import {translations} from '../../../../../../i18n/translations';
import {AppLanguageTag} from '../../../../../../i18n/types';
import {PaginatedTableData} from '../../../../../../interfaces/paginated-table-data';
import {ApplicationsRepository} from '../../../../../../repositories/applications';
import i18n from 'i18next';
import {CaregiverMode} from '../../../../../../enums';
import {ApplicationsStateModal} from '../../../../../../components/user-applications/modals/applications-state-modal';
import GenericHeader from '../../../../../../components/generic-header/generic-header';
import {Box, Stack} from '@mui/material';
import SearchInput from '../../../../../../components/search-input/search-input';

// Columns IDs
enum ColumnIdentifier {
  Name = 'name',
  Type = 'type',
  State = 'state',
}

interface ApplicationUsersProfessionalCaregiverOfficeScreenProps {
  canWrite: boolean;
  caregiverMode: CaregiverMode.Professional | CaregiverMode.SystemAdministrator;
}

export default function ApplicationUsersProfessionalCaregiverOfficeScreen({
  canWrite,
  caregiverMode,
}: ApplicationUsersProfessionalCaregiverOfficeScreenProps) {
  const snackBarContext = useSnackBar();
  const officeContext = useOffice();
  const [updateApplicationUserDialogOpened, setUpdateApplicationUserDialogOpened] = useState<{
    disableDialogOpened: boolean;
    enableDialogOpened: boolean;
  }>({disableDialogOpened: false, enableDialogOpened: false});
  const dataToUpdateRef: MutableRefObject<
    {userId: string; userName: string; applicationActiveFlag: boolean} | undefined
  > = useRef();
  let {url} = useRouteMatch();

  // Get params
  let {entityId, applicationId}: {entityId: string; applicationId: string} = useParams();

  const {t} = useTranslation();

  // Fetch users
  const handleGetUsers = async (limit: number, skip: number, search?: string) => {
    // Load users
    const result: {totalCount: number; data: Array<ApplicationUser>} =
      await ApplicationsRepository.instance.getApplicationUsersInEntityAsAffiliatedForEntity(
        entityId,
        applicationId,
        skip,
        limit,
        search
      );

    // Process data
    const processedData: PaginatedTableData<ApplicationUser> = result.data.map((obj) => {
      // Link
      let toLink: string = '';
      if (obj.user.type === UserType.Senior) {
        toLink = `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_Seniors}/${obj.user.id}`;
      } else if (obj.user.type === UserType.Caregiver) {
        toLink = `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}/${obj.user.id}`;
      }

      // Return row values
      return {
        id: obj.user.id || '',
        item: obj,
        avatarUrl: obj.user.picture,
        enabled: obj.isActive === true ? true : false,
        toLink: toLink,
        columns: [
          {
            columnId: ColumnIdentifier.Name,
            value: obj.user.name || '',
          },
          {
            columnId: ColumnIdentifier.Type,
            value: obj.user.type ? t(translations.user_types[obj.user.type]) : '',
          },
          {
            columnId: ColumnIdentifier.State,
            value:
              obj.isActive === true
                ? t(translations.applications.professional_caregiver.edit.users.state_value_true)
                : t(translations.applications.professional_caregiver.edit.users.state_value_false),
          },
        ],
      };
    });

    // Return result
    return {
      data: processedData,
      totalCount: result.totalCount,
    };
  };

  // Use the paginated table hook
  const {
    data,
    isLoading,
    error,
    pagesCount,
    currentPage,
    currentSearch,
    handlePageChange,
    handleApplyFilters,
    handleUpdateCurrentSearch,
    handleUpdateItem,
  } = usePaginatedTable<ApplicationUser>({
    fetchData: handleGetUsers,
  });

  const handleAskUpdateApplicationUserState = (userId: string, value: boolean) => {
    // Get user name
    const userName: string | undefined = data
      .find((obj) => obj.id === userId)
      ?.columns.find((obj) => obj.columnId === ColumnIdentifier.Name)?.value;

    // Set user info for later
    dataToUpdateRef.current = {
      userId: userId,
      userName: userName || '',
      applicationActiveFlag: value,
    };

    // Open modal
    setUpdateApplicationUserDialogOpened({
      disableDialogOpened: !value,
      enableDialogOpened: value,
    });
  };

  const handleDeclineUpdateApplicationUserState = () => {
    // Close modal
    setUpdateApplicationUserDialogOpened({
      disableDialogOpened: false,
      enableDialogOpened: false,
    });

    // Clear
    dataToUpdateRef.current = undefined;
  };

  const handleConfirmUpdateApplicationUserState = () => {
    // Close modal
    setUpdateApplicationUserDialogOpened({
      disableDialogOpened: false,
      enableDialogOpened: false,
    });

    // Update
    handleUpdateApplicationUserState();
  };

  const handleUpdateApplicationUserState = () => {
    if (dataToUpdateRef.current) {
      // Keep values
      const userId = dataToUpdateRef.current.userId;
      const value = dataToUpdateRef.current.applicationActiveFlag;

      // Clear
      dataToUpdateRef.current = undefined;

      // Updated item
      let updatedItem = data.find((obj) => obj.id === userId)?.item;
      if (updatedItem) {
        updatedItem.isActive = value;
      }

      // Update item in UI
      handleUpdateItem(
        userId,
        [
          {
            updatedColumnId: ColumnIdentifier.State,
            updatedColumnValue: value
              ? t(translations.applications.professional_caregiver.edit.users.state_value_true)
              : t(translations.applications.professional_caregiver.edit.users.state_value_false),
          },
        ],
        updatedItem,
        value
      );

      // Make request to the backend
      // Note: Don't await because it will take too much time to update
      continueUpdateApplicationUserState(userId, applicationId, value);
    } else {
      // Error
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(
          translations.applications.professional_caregiver.edit.users.errors
            .update_state_application_error_message
        ),
      });

      // Not expected. Log.
      console.debug(
        '🔴 Could not update the state of the application. Information was missing to perform the request.'
      );
    }
  };

  const continueUpdateApplicationUserState = async (
    userId: string,
    applicationId: string,
    value: boolean
  ) => {
    try {
      // Make request to the backend
      await ApplicationsRepository.instance.updateActiveStateForUser(userId, applicationId, value);
    } catch (error) {
      // Old flag
      const oldFlag: boolean = !value;

      // Updated item
      let updatedItem = data.find((obj) => obj.id === userId)?.item;
      if (updatedItem) {
        updatedItem.isActive = oldFlag;
      }

      // Change item back
      handleUpdateItem(
        userId,
        [
          {
            updatedColumnId: ColumnIdentifier.State,
            updatedColumnValue: oldFlag
              ? t(translations.applications.professional_caregiver.edit.users.state_value_true)
              : t(translations.applications.professional_caregiver.edit.users.state_value_false),
          },
        ],
        updatedItem,
        oldFlag
      );

      // Error
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: value
          ? t(
              translations.applications.professional_caregiver.edit.users.errors
                .enable_application_error_message
            )
          : t(
              translations.applications.professional_caregiver.edit.users.errors
                .disable_application_error_message
            ),
      });

      // Log
      console.debug(
        `🔴 Could not update the state of the application with id ${applicationId} to ${value} for user with id ${userId}.`,
        error
      );
    }
  };

  // Get application name
  let applicationNameLocalized: string = '';
  switch (i18n.language) {
    case AppLanguageTag.ptPT:
      applicationNameLocalized =
        officeContext.state.applicationForm?.nameLocalized?.ptPT ||
        officeContext.state.applicationForm?.name ||
        '';
      break;
    case AppLanguageTag.esES:
      applicationNameLocalized =
        officeContext.state.applicationForm?.nameLocalized?.esES ||
        officeContext.state.applicationForm?.name ||
        '';
      break;
    case AppLanguageTag.glES:
      applicationNameLocalized =
        officeContext.state.applicationForm?.nameLocalized?.glES ||
        officeContext.state.applicationForm?.name ||
        '';
      break;
    case AppLanguageTag.enUS:
      applicationNameLocalized =
        officeContext.state.applicationForm?.nameLocalized?.enUS ||
        officeContext.state.applicationForm?.name ||
        '';
      break;
    case AppLanguageTag.ptBR:
      applicationNameLocalized =
        officeContext.state.applicationForm?.nameLocalized?.ptBR ||
        officeContext.state.applicationForm?.name ||
        '';
      break;
    default:
      applicationNameLocalized = officeContext.state.applicationForm?.name || '';
      break;
  }

  return (
    <>
      <GenericHeader
        isLoading={false}
        titleCrumbs={[
          {
            label: t(translations.office.professional_caregiver.tabs.digital_inclusion),
            link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion}`,
          },
          {
            label: t(translations.office.professional_caregiver.tabs.applications),
            link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion_Applications}`,
          },
          {
            label: applicationNameLocalized,
            link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion_Applications}/${applicationId}/details`,
          },
          {
            label: t(translations.application_tabs.tabs.users),
            link: url,
          },
        ]}
        leftColumn={
          <Stack
            gap={2}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              width: '100%',
              paddingTop: '10px',
            }}
          >
            <SearchInput
              placeholder={t(
                translations.applications.professional_caregiver.edit.users.search_input_placeholder
              )}
              value={currentSearch}
              onChange={(event) => handleUpdateCurrentSearch(event.target.value)}
              onSearchAction={(value: string) => handleApplyFilters(1, value)}
            />
          </Stack>
        }
        rightColumn={null}
      />
      <Box className="listing-container">
        <Box className="listing-container-centered">
          <PaginatedTable
            avatar={{
              type: 'user',
            }}
            emptyTableMessage={t(
              translations.applications.professional_caregiver.edit.users.no_users
            )}
            columns={[
              {
                columnId: ColumnIdentifier.Name,
                title: t(translations.applications.professional_caregiver.edit.users.name_header),
                flexGrow: 2,
              },
              {
                columnId: ColumnIdentifier.Type,
                title: t(translations.applications.professional_caregiver.edit.users.type_header),
                flexGrow: 1,
              },
              {
                columnId: ColumnIdentifier.State,
                title: t(translations.applications.professional_caregiver.edit.users.state_header),
                flexGrow: 1,
                button: {
                  type: 'toggle',
                  showColumnValue: true,
                  toggle: {
                    enabledValue: t(
                      translations.applications.professional_caregiver.edit.users.state_value_true
                    ),
                    handleClick: handleAskUpdateApplicationUserState,
                  },
                  disabled: !canWrite,
                  disabledTooltip: t(translations.user_applications.disabled_state_tooltip),
                },
              },
            ]}
            rows={data}
            isLoading={isLoading}
            error={error}
            pagesCount={pagesCount}
            currentPage={currentPage}
            handlePageChange={handlePageChange}
            linkableRows={caregiverMode !== CaregiverMode.Professional}
          />
        </Box>
      </Box>
      <ApplicationsStateModal
        open={
          updateApplicationUserDialogOpened.disableDialogOpened ||
          updateApplicationUserDialogOpened.enableDialogOpened
        }
        state={updateApplicationUserDialogOpened.disableDialogOpened ? 'disable' : 'enable'}
        onClose={handleDeclineUpdateApplicationUserState}
        onConfirm={handleConfirmUpdateApplicationUserState}
        applicationName={applicationNameLocalized}
        description={t(
          translations.applications.professional_caregiver.edit.users[
            `${updateApplicationUserDialogOpened.disableDialogOpened ? 'disable' : 'enable'}_application_dialog_message`
          ],
          {
            name: dataToUpdateRef.current?.userName || '',
          }
        )}
      />
    </>
  );
}
